<template lang="pug">
mixin options
  //- OPTIONS
  fieldset.grid-question__options.relative.grid.gap-2.lg_gap-0.lg_items-center.lg_flex.lg_border-b-2&attributes(attributes)(
    :style="{ gridTemplateColumns: `repeat(auto-fill, minmax(200px, 1fr))` }"
    class="lg_border-primary-700/30"
    :aria-labelledby="getItemId(itemIndex)"
  )
    //- OPTION
    .grid-question__option.bg-gray-100.rounded-lg.lg_flex-1.lg_h-full.dark_bg-gray-900.lg_bg-transparent.lg_rounded-none(
      :class="[getOptionInputValue(itemIndex, optionIndex) === 1 || screen.survey ? 'correct' : 'incorrect']"
      class="lg_dark_bg-gray-900/50"
      v-for="(option, optionIndex) in screen.options"
    )
      //- LABEL
      label.grid-question-option__label.relative.flex.items-center.lg_justify-center.w-full.h-full.p-4.transition.lg_p-0(
        :class="{ 'cursor-pointer ripple': !isSubmitted && !isEditModeActive && !isOptionSelected(itemIndex, optionIndex) && !isWrongAttempt(itemIndex, optionIndex), 'overflow-hidden': !isEditModeActive }"
      )
        .ripple__wave

        //- INPUT ELEMENT
        input.sr-only.peer(
          :aria-label="`${item.text}:  ${option.text}`"
          :checked="isOptionSelected(itemIndex, optionIndex)"
          :disabled="isSubmitted || isEditModeActive || isWrongAttempt(itemIndex, optionIndex)"
          :id="getOptionId(itemIndex, optionIndex)"
          :key="getOptionId(itemIndex, optionIndex)"
          :value="getOptionInputValue(itemIndex, optionIndex)"
          @change="changeHandler"
          :type="isMulti ? 'checkbox' : 'radio'"
          uc-word-skip=""
          :name="getItemId(itemIndex)"
        )
        uc-text.sr-only(
          text="correct selected"
          v-if="isSubmitted && isOptionSelected(itemIndex, optionIndex) && isOptionCorrect(itemIndex, optionIndex)"
        )
        uc-text.sr-only(
          text="incorrect selected"
          v-else-if="isSubmitted && isOptionSelected(itemIndex, optionIndex) && !isOptionCorrect(itemIndex, optionIndex)"
        )

        //- CHECKBOX/RADIO
        .grid-question-option__checkbox.transition.aspect-square.lg_m-auto.lg_inset-0(
          class="border-primary-700/70 text-primary-700 peer-focus-visible_ring-4 dark_border-primary-600/70 dark_text-primary-500 m-1 inline-flex flex-none h-4 w-4 items-center justify-center border-[1.5px] text-sm"
          :class="[{ 'opacity-20': (!isRetryAllowed && isSubmitted && !isOptionCorrect(itemIndex, optionIndex)) || (isRetryAllowed && isWrongAttempt(itemIndex, optionIndex)) || isEditModeActive, 'opacity-15': isRetryAllowed && !isSubmitted && isWrongAttempt(itemIndex, optionIndex) }, isMulti ? 'rounded-sm' : 'rounded-full']"
        )
          transition-pop(:duration="0.15")
            .inline-flex.items-center.justify-center(
              v-if="isOptionSelected(itemIndex, optionIndex)"
            )
              uc-icon.scale-75(
                :active="isOptionSelected(itemIndex, optionIndex)"
                icon="mdi:circle"
                v-if="!isMulti"
              )
              uc-icon(
                :active="isOptionSelected(itemIndex, optionIndex)"
                icon="mdi:check-bold"
                v-else=""
              )

        //- used for the option hover state and correct option highlighting
        .grid-question-option__background.transition.rounded-lg.fill.lg_rounded-none(
          :class="{ disable: isSubmitted || isWrongAttempt(itemIndex, optionIndex) || (!isMulti && isOptionSelected(itemIndex, optionIndex)), 'lg_border-none lg_bg-primary-200/50 lg_dark_bg-primary-700/20 border-2 border-primary-500': (isSubmitted && isOptionCorrect(itemIndex, optionIndex) && (!isRetryAllowed || !hasAttemptsRemaining)) || (isOptionCorrect(itemIndex, optionIndex) && isEditModeActive) }"
        )

        //- OPTION TEXT (ON SMALLER DEVICES)
        .grid-question-option__text.prose-stack.ps-4.lg_p-0.lg_m-0.lg_sr-only(
          :uc-word-bold="(isRetryAllowed || wordDoc) && isOptionCorrect(itemIndex, optionIndex) && highlightAnswers ? true : null"
        )
          //- detect when selected and toggle a pe-1 padding to resolve the layout jumping when the text toggles bold on/off
          uc-editable.text-black.dark_text-white.opacity-80(
            :class="{ 'opacity-30': (isSubmitted && !isOptionCorrect(itemIndex, optionIndex) && (!isRetryAllowed || !hasAttemptsRemaining)) || isWrongAttempt(itemIndex, optionIndex), 'opacity-100 font-semibold': isSubmitted ? isOptionCorrect(itemIndex, optionIndex) && (!isRetryAllowed || !hasAttemptsRemaining || isOptionSelected(itemIndex, optionIndex)) : isOptionSelected(itemIndex, optionIndex), 'pe-1': !isOptionSelected(itemIndex, optionIndex) }"
            :edit-path="`${editPath}.options.${optionIndex}.text`"
            :k="`${option.id}.text`"
            :text="option.text"
            aria-hidden="true"
          )
          uc-text(
            :k="`${option.id}.text`"
            :text="`* ${option.text}`"
            v-if="wordDoc"
          )

        .ripple__wave

        //- used for the option hover state, disable once submitted
        .grid-question-option__background.transition.rounded-lg.fill.lg_rounded-none(
          :class="{ disable: isSubmitted || isWrongAttempt(itemIndex, optionIndex) || (!isMulti && isOptionSelected(itemIndex, optionIndex)), 'lg_border-none lg_bg-primary-200/50 lg_dark_bg-primary-700/20 border-2 border-primary-500': isSubmitted && isOptionCorrect(itemIndex, optionIndex) && (!isRetryAllowed || !hasAttemptsRemaining) }"
          :data-sfx="isMulti ? 'select' : 'click1'"
        )

    //- MARKING ICON (desktop: end of item row)
    transition-pop
      .grid-question-item__icon.absolute.items-center.hidden.-m-4.lg_flex.ltr_right-0.rtl_left-0(
        :class="[isItemAllCorrect(item, itemIndex) || (screen.survey && isItemIncluded(itemIndex)) ? 'bg-primary-500' : 'bg-gray-500']"
        v-if="isSubmitted && isItemIncluded(itemIndex)"
      )
        uc-icon.text-white(
          icon="theme:check"
          v-if="isItemAllCorrect(item, itemIndex) || (screen.survey && isItemIncluded(itemIndex))"
        )
        uc-icon.text-white(
          icon="theme:error"
          v-if="!isItemAllCorrect(item, itemIndex) && !screen.survey"
        )

//- END OPTIONS MIXIN

//- FORM
form.grid-question.w-full.text-lg.dont-unlock.break-inside-avoid(
  :class="{ '-is-submitted': isSubmitted, '-all-correct': isSubmitted && allCorrect }"
  :data-completed="isSubmitted"
  :data-id="screen.id"
  :id="`q_${screen.id}`"
  ref="el"
)
  //- QUESTION TEXT
  .grid-question__text.prose-stack.mb-8.fade-in
    uc-editable.font-bold(
      :editPath="`${editPath}.question`"
      :k="`${screen.id}.question`"
      :text="screen.question"
    )
  uc-preload-sfx(name="click1")
  uc-preload-sfx(name="select")
  //- ITEMS
  .grid-question__items.flex.flex-col.w-full.gap-10.lg_bg-paper.lg_grid.lg_gap-0.md_p-5.lg_p-10.fade-in(
    :style="{ gridTemplateRows: `repeat(${screen.items.length + 1}, auto)`, gridTemplateColumns: 'auto auto' }"
    v-if="isValidItemData && !audioDoc"
  )
    //- OPTIONS HEADER (LARGER DEVICES)
    .items__options-labels.hidden.col-start-2.mb-4.font-bold.text-center.text-black.rounded.corners.dark_text-white.items-center.hyphens-auto.lg_grid.gap-2(
      :class="[{ 'break-words': suspend.locale !== 'en-GB' }]"
      :style="{ gridTemplateColumns: `repeat(${screen.options.length}, minmax(80px, 1fr))` }"
    )
      uc-editable.prose-stack.items__options-label(
        class="!m-0"
        :edit-path="`${editPath}.options.${optionIndex}.text`"
        :k="`${option.id}.text`"
        :text="option.text"
        type="span"
        v-for="(option, optionIndex) in screen.options"
        v-if="!wordDoc"
      )

    //- ITEM
    template(v-for="(item, itemIndex) in screen.items")
      //- ITEM TEXT
      .item__text.relative.items-center.col-start-1.p-10.text-black.rounded-md.lg_flex-auto.dark_text-white.text-center.lg_text-left.rtl_lg_text-right.lg_border-b-2.lg_p-4.lg_rounded-none(
        :class="[{ 'bg-paper': !largeDevice }]"
        class="border-primary-700/30 lg_dark_bg-gray-900/50"
        :id="getItemId(itemIndex)"
      )
        uc-editable.prose-stack.mb-4.lg_m-0(
          :edit-path="`${editPath}.items.${itemIndex}.text`"
          :k="`${item.id}.text`"
          :text="item.text"
        )

        //- USE OPTIONS MIXIN (FOR SMALLER DEVICES)
        +options.lg_hidden.fade-in

        //- MARKING ICON (mobile: fixed to item bottom right)
        .grid-question-item__icon.absolute.top-0.left-0.flex.items-center.-m-3.lg_hidden(
          :class="[isItemAllCorrect(item, itemIndex) || (screen.survey && isItemIncluded(itemIndex)) ? 'bg-primary-500' : 'bg-gray-500']"
          v-if="isSubmitted && isItemIncluded(itemIndex)"
        )
          uc-icon.text-white(
            icon="theme:check"
            v-if="isItemAllCorrect(item, itemIndex) || (screen.survey && isItemIncluded(itemIndex))"
          )
          uc-icon.text-white(
            icon="theme:error"
            v-if="!isItemAllCorrect(item, itemIndex) && !screen.survey"
          )

      //- USE OPTIONS MIXIN (FOR LARGE DEVICES)
      +options.hidden.fade-in(v-if="!wordDoc")

  //- SUBMIT BUTTON
  .grid-question__submit-wrapper
    .grid-question__submit-btn.flex.justify-end.items-center.mt-8.group(
      :class="[{ 'hidden': isSubmitted }, smallDevice && 'flex-wrap gap-2']"
    )
      uc-preload-sfx(name="yes")
      uc-preload-sfx(name="wrong")
      uc-preload-sfx(name="survey-submit")
      uc-button.grid-question__submit.btn.btn-primary.ripple(
        :class="smallDevice && 'w-full'"
        :disabled="disableSubmit"
        ref="submit"
        @click="submitHandler"
        :data-sfx="screen.survey ? 'survey-submit' : allCorrect ? 'yes' : 'wrong'"
      )
        span.ripple__wave
        uc-text(text="Submit")

  //- FEEDBACK TEXT
  .grid-question__feedback.flex-1.mt-8.fade-in(
    ref="feedbackRef"
    v-if="feedback && !audioDoc"
    aria-live="polite"
    aria-atomic="true"
    role="status"
  )
    transition-expand(
      :delay="isRetryAllowed ? 0 : 0.5"
      :duration="1"
      scroll=""
      v-if="!lightbox"
    )
      .grid-question__feedback-content.block.outline-2(
        v-if="isSubmitted || wordDoc"
      )
        //- expose these strings for the translate script
        //- text="Feedback"
        //- text="That’s right!"
        //- text="Not quite."
        uc-text.prose.md_prose-lg.text-primary-700.dark_text-primary-300.font-bold.italic.feedback-heading.mb-4(
          :k="wordDoc || screen.survey ? 'Feedback' : allCorrect ? 'That’s right!' : 'Not quite.'"
          :text="wordDoc || screen.survey ? 'Feedback' : allCorrect ? 'That’s right!' : 'Not quite.'"
          type="p"
          uc-dont-read=""
          v-if="!wordDoc && !hideFeedbackTitle"
        )

        uc-editable.prose.dark_prose-invert(
          :editPath="feedback?.path ? `${editPath}.${feedback.path}` : `${editPath}.feedback`"
          :k="feedback?.path && `${screen.id}.${feedback.path}`"
          :text="feedback?.text"
          type="p"
          uc-dont-read=""
          uc-word-skip=""
        )
        slot

    //- retry button (under feedback text)
    .group.mt-8.question__retry-btn.flex.items-center.gap-6(
      :class="smallDevice && 'flex-wrap gap-2'"
      v-if="isSubmitted && isRetryAllowed && hasAttemptsRemaining && !allCorrect"
    )
      uc-preload-sfx(name="continue")
      uc-button.btn.btn-primary.question__retry.ripple(
        :class="smallDevice && 'w-full'"
        @click="retryQuestion"
        icon="mdi:refresh"
        text="Try again"
        data-sfx="continue"
      )

    //- show feedback button (for lightbox feedback)
    .group.mt-8.question__retry-btn.flex.items-center.gap-6(
      :class="smallDevice && 'flex-wrap gap-2'"
      v-if="lightbox && isSubmitted"
    )
      uc-button.btn.btn-primary.primary-green.question__retry.ripple(
        :class="smallDevice && 'w-full'"
        @click="showFeedback"
        text="Show feedback"
        data-sfx="continue"
      )

  //- render all conditional feedback (if any) for word docs and audio docs
  div(v-for="[k, v] in allConditionalFeedback" v-if="!lightbox && (wordDoc || audioDoc)")
    template(v-if="v.trim()")
      audio-marker(:name="`${screen.id}-${k}`" v-if="k === 'feedback'")
      audio-marker(:name="`${screen.id}-feedback-${k}`" v-else="")
      p(uc-dont-read="" v-if="!audioDoc")
        uc-text.font-bold(text="Feedback") 

        span &nbsp;

        //- expose these strings for the translate script 
        //- text="(correct)"
        //- text="(incorrect)"
        uc-text.font-bold(:text="`(${k})`" v-if="k !== 'feedback'") 

      uc-text(
        :k="`${screen.id}.feedback`"
        :text="v"
        type="p"
        v-if="k === 'feedback'"
      )
      uc-text(:k="`${screen.id}.feedback.${k}`" :text="v" type="p" v-else="")
  slot
</template>

<script lang="ts">
export default {
  inheritAttrs: false
};
</script>

<script setup lang="ts">
import {
  nextTick,
  ref,
  computed,
  unref,
  inject,
  reactive,
  watch,
  toRef
} from 'vue';
import { set, setCompleted } from '../lib/SCORM';
import gsap from 'gsap';
import useSuspend from '../lib/useSuspend';
import { play, stop } from '../lib/audio';
import useQuizzes from '../stores/useQuizzes';
import bugAlert from '../lib/bugAlert';
import { breakpointsTailwind, useBreakpoints } from '@vueuse/core';
import openLightbox from '../lib/openLightbox';
import { saveInsightsData } from '../lib/insightsData';
import useScreenAudio from '../lib/useScreenAudio';
import gridQuestionSchema from '../lib/screen-schemas/screen-grid-question.schema';

type Item = ScreenGridQuestion['items'][0];
type Option = ScreenGridQuestion['options'][0];

let wrongAttemptedOptions: string[] = reactive([]);

const audioDoc = inject('audioDoc') as boolean;
const wordDoc = inject('wordDoc') as boolean;
const highlightAnswers = inject('highlightAnswers') as boolean;
const props = defineProps<{ editPath: string; screen: ScreenGridQuestion }>();
const submit = ref<HTMLElement>();
const suspend = useSuspend();
const sectionId = inject('sectionId') as string;
const attemptCounter = ref(0);
const submittedState = ref(false);
const el = ref<HTMLElement>();
const { register } = useQuizzes();
const breakpoints = useBreakpoints(breakpointsTailwind);
const smallDevice = breakpoints.smallerOrEqual('sm');
const largeDevice = breakpoints.greaterOrEqual('lg');
const isEditModeActive = inject('isEditModeActive') as Ref<boolean>;
const lightbox = computed(() => props.screen['feedback-layout'] === 'lightbox');

const pageId = inject('pageId') as object;
register(props.screen, pageId);

const isKitchenSink = inject<Ref<boolean>>('isKitchenSink');

const hideFeedbackTitle = computed(() => {
  return props.screen['feedback-no-title'] ?? false;
});

/*
 * Validate item answer lengths in the YAML
 */
const isValidItemData = computed(() => {
  // check that we have specified feedback text if we are using attempts
  if (isRetryAllowed.value && !props.screen.feedback) {
    console.log(
      props.screen.id,
      'isValidData',
      isRetryAllowed.value,
      feedback.value
    );
    bugAlert(
      `Screen #${sectionId}\n\nYou have specifed to use attempts but there is no feedback. Feedback is required.\n\nattempts: ${props.screen.attempts}`
    );

    if (import.meta.env.DEV) return false;
  }

  if (isRetryAllowed.value && props.screen['feedback-layout'] === 'replace') {
    bugAlert(
      `Screen #${sectionId}\n\nYou have specifed the question \`#${props.screen.id}\` with \`attempts: ${props.screen.attempts}\` with \`feedback-layout: replace\`, which are not allowed together. Please remove one of those two props.`
    );

    if (import.meta.env.DEV) return false;
  }

  if (
    props.screen['feedback-layout'] === 'lightbox' &&
    el.value?.closest('.lightbox')
  ) {
    bugAlert(
      `Screen #${props.screen.id}\n\nYou have used feedback-layout: lighbox inside a lightbox screen. Pleas use another feedback-layout type.`
    );

    if (import.meta.env.DEV) return false;
  }

  props.screen.items.every((item: Item, itemIndex: number) => {
    const itemAnswers = item.answer.split(' ');
    if (itemAnswers.length !== props.screen.options.length) {
      bugAlert(
        `#${sectionId}\n\n${item.text}\n\nNumber of item answers (${item.answer}) does not match number of options (${props.screen.options.length})`
      );

      if (import.meta.env.DEV) return false;
    }
  });

  return true;
});

/*
 * Count the correct answers (1s) per item to see if there is more than one
 * if so, we use checkboxes
 */
const isMulti = computed(() => {
  let multi = false;
  if (props.screen.survey === 'multi') return true;
  if (props.screen.survey || props.screen.survey === 'single') return false;
  props.screen.items.every((item: Item, itemIndex: number) => {
    if (countItemCorrectAnswers(item) > 1) {
      multi = true;
      return false;
    } else {
      return true;
    }
  });

  return multi;
});

const selected = computed(() => {
  return suspend.questionOptions[props.screen.id] || [];
});

const isSubmitted = computed(() => {
  if (isRetryAllowed.value) {
    return submittedState.value;
  } else {
    return Boolean(suspend.completed[props.screen.id]);
  }
});

/**
 * Have we selected enough options to enable the Submit button
 * Check for at least one selected option per item, return false if fewer
 */
const isReadyToSubmit = computed(() => {
  // multi survey questions can submit empty
  if (props.screen.survey === 'multi') {
    return !props.screen['select-count']
      ? true
      : props.screen['select-count'] > selected.value.length
        ? false
        : true;
  }

  return props.screen.items.every((item: Item, itemIndex: number) => {
    // any selected options with matching item index
    let itemSelections = selected.value.filter(
      itemID => itemID.indexOf('_i' + itemIndex) !== -1
    );

    return itemSelections.length > 0;
  });
});

const disableSubmit = computed(() => {
  if (isEditModeActive.value) return false;
  return !isReadyToSubmit.value || isSubmitted.value;
});

/**
 * returns false if any item is incorrect
 */
const allCorrect = computed(() => {
  let allResult = true;
  // surveys are always correct
  if (props.screen.survey) return allResult;

  props.screen.items.every((item: Item, itemIndex: number) => {
    return (allResult = isItemAllCorrect(item, itemIndex));
  });

  return allResult;
});

// 0 is unlimted attempts 2+ is limited (1 is the same as normal)
const isRetryAllowed = computed(() => {
  const maxAttempts = props.screen?.attempts ?? -1;
  return Boolean(maxAttempts === 0 || maxAttempts > 1);
});

const hasAttemptsRemaining = computed(() => {
  const maxAttempts = props.screen?.attempts ?? -1;

  if (allCorrect.value) {
    return false;
  } else if (maxAttempts === 0 || maxAttempts === -1) {
    return !allCorrect.value;
  } else {
    return attemptCounter.value < maxAttempts;
  }
});

const isFinalAttempt = computed(() => {
  const maxAttempts = props.screen?.attempts ?? -1;
  return Boolean(attemptCounter.value === maxAttempts - 1);
});

const feedback = computed(() => getFeedbackString(props.screen.feedback));

function getOptionInputValue(itemIndex: number, optionIndex: number) {
  if (import.meta.env.DEV) {
    return isOptionCorrect(itemIndex, optionIndex) ? 1 : 0;
  }

  return props.screen.options[optionIndex].text;
}

function getOptionId(itemIndex: number, optionIndex: number) {
  return `${props.screen.id}_i${itemIndex}_o${optionIndex}`;
}

function getItemId(itemIndex: number) {
  return `${props.screen.id}_i${itemIndex}`;
}

function isOptionCorrect(itemIndex: number, optionIndex: number) {
  // for surveys only selected options are correct
  if (props.screen.survey && !isOptionSelected(itemIndex, optionIndex))
    return false;

  // "1 0 0 0" to [1,0,0,0]
  const itemAnswers = props.screen.items[itemIndex].answer.split(' ');
  const optionAnswer = Boolean(itemAnswers[optionIndex] === '1');

  return optionAnswer;
}

function isOptionSelected(itemIndex: number, optionIndex: number) {
  return selected.value.includes(getOptionId(itemIndex, optionIndex));
}

function countItemCorrectAnswers(item: Item) {
  const itemAnswers = item.answer.split(' ');
  return itemAnswers.filter(optionAnswer => optionAnswer === '1').length;
}

function isItemAllCorrect(item: Item, itemIndex: number) {
  // "1 0 0 0" to [1,0,0,0]
  const itemAnswers = item.answer.split(' ');
  let itemAllCorrect = true;

  // return false if any of the item's options are incorrect
  props.screen.options.every((option: Option, optionIndex: number) => {
    const optionSelected = isOptionSelected(itemIndex, optionIndex);
    const optionAnswer = Boolean(itemAnswers[optionIndex] === '1');

    if (
      (optionAnswer && optionSelected) ||
      (!optionAnswer && !optionSelected)
    ) {
      return true;
    } else {
      itemAllCorrect = false;
      return false;
    }
  });

  return itemAllCorrect;
}

function isWrongAttempt(itemIndex: number, optionIndex: number) {
  return (
    isRetryAllowed.value &&
    wrongAttemptedOptions.includes(getOptionId(itemIndex, optionIndex))
  );
}

function updateWrongAttempts() {
  props.screen.items.forEach((item: Item, itemIndex: number) => {
    props.screen.options.forEach((option: Option, optionIndex: number) => {
      const correct = isOptionCorrect(itemIndex, optionIndex);
      const checked = isOptionSelected(itemIndex, optionIndex);
      if (checked && !correct) {
        wrongAttemptedOptions.push(getOptionId(itemIndex, optionIndex));
      }
    });
  });
}

function retryQuestion() {
  stop();

  submittedState.value = false;

  let sel = unref(selected);

  // remove all wrong attempts from the current selections
  sel = sel.filter(itemID => !wrongAttemptedOptions.includes(itemID));

  set(`questionOptions.${props.screen.id}`, sel);

  if (el.value) el.value?.focus();
}

function showFeedback() {
  if (lightbox.value) {
    openLightbox(feedback.value.text);
  }
}

// only used in the KS
function resetQuestion() {
  submittedState.value = false;
  attemptCounter.value = 0;
  wrongAttemptedOptions = [];

  delete suspend.questionOptions[props.screen.id];

  if (isKitchenSink?.value) el.value?.focus();
}

// the KS has a reset screen button, so listen for the resetting of the screen's completion
watch(
  () => suspend.completed[props.screen.id],
  (newVal, oldVal) => {
    if (oldVal && !newVal && isRetryAllowed.value) {
      resetQuestion();
    }
  }
);

function changeHandler(e: Event & { target: HTMLInputElement }) {
  let sel = unref(selected);
  const selectedOption = e.target;

  if (selectedOption.checked || selectedOption.type === 'radio') {
    if (!isMulti.value) {
      // option IDs are in the format {screen.id}_i0_o0
      // match the number after the "_i" for the item index
      const selectedItemIndex = selectedOption.id.match(/.*_i(\d+)/i)[1];

      // remove all previous options matching this item's index
      sel = sel.filter(
        itemID => itemID.indexOf('_i' + selectedItemIndex) === -1
      );
    }

    // insert the selected option ID to the front of the array
    sel.unshift(selectedOption.id);
  } else {
    // find and remove the deselected ID from the array
    sel.splice(sel.indexOf(selectedOption.id), 1);
  }

  set(`questionOptions.${props.screen.id}`, sel);
}

function delayNextButton(delay) {
  const section = el.value?.closest('.section') as HTMLElement;
  const nextButton = section?.querySelector(
    '.screen-next-button'
  ) as HTMLElement;

  if (nextButton)
    nextButton.style.transitionDelay = suspend.reducedMotion
      ? '0s'
      : `${delay}s`;
}

async function submitHandler(e: Event) {
  attemptCounter.value++;

  submittedState.value = true;

  updateWrongAttempts();

  const maxAttempts = props.screen?.attempts ?? -1;
  const { id } = props.screen;
  const isComplete = isRetryAllowed.value
    ? allCorrect.value || !hasAttemptsRemaining.value
    : true;

  /*console.log(
    isRetryAllowed.value,
    attemptCounter.value,
    maxAttempts,
    hasAttemptsRemaining.value,
    isComplete
  );*/

  // we need to apply the delay before completion as it relies on a transtion-delay for the auto-alpha-1 CSS class
  delayNextButton(feedback.value ? 1 : 0.5);

  if (isComplete) setCompleted(id);

  await nextTick();

  set(`questionOptions.${id}`, selected.value);
  set(`correctQuestions.${id}`, allCorrect.value);

  await new Promise(r => gsap.delayedCall(props.screen.feedback ? 0.5 : 0, r));

  if (isComplete) {
    setCompleted(sectionId);

    saveInsightsData(props.screen, {
      correct: allCorrect.value
    });
  }

  if (lightbox.value) {
    openLightbox(feedback.value.text);
  } else {
    playFeedbackAudio();
  }
}

/**
 * Returns an object `{ text, path }`, computed from the given feedback prop and the status of the user's selection.
 *
 * The `text` is the relevant feedback string to display. The `path` is where in the YAML the string was found, for the purpose of finding its translation key, e.g., `feeback` or `feedback.correct`.
 *
 * @param f The feedback string, or object when conditional
 */
function getFeedbackString(f: ScreenGridQuestion['feedback']): {
  text: string;
  path: string;
  audio: string;
} | null {
  // no feedback
  if (!f) return null;

  // single feedback text
  if (typeof f === 'string')
    return { text: f, path: 'feedback', audio: 'feedback' };

  // correct
  if (f.correct && allCorrect.value)
    return {
      text: f.correct,
      path: 'feedback.correct',
      audio: 'feedback-correct'
    };

  // retry-final
  if (f['retry-final'] && !allCorrect.value && isFinalAttempt.value)
    return {
      text: f['retry-final'],
      path: 'feedback.retry-final',
      audio: 'feedback-retry-final'
    };

  // retry
  if (f['retry'] && !allCorrect.value && hasAttemptsRemaining.value)
    return {
      text: f['retry'],
      path: 'feedback.retry',
      audio: 'feedback-retry'
    };

  // incorrect
  if (f.incorrect && !allCorrect.value)
    return {
      text: f.incorrect,
      path: 'feedback.incorrect',
      audio: 'feedback-incorrect'
    };

  return null;
}

const allConditionalFeedback = computed<[string, string][]>(() => {
  if (typeof props.screen.feedback === 'string')
    return [['feedback', props.screen.feedback]];

  return Object.entries(props.screen.feedback || {});
});

const { playSlot } = useScreenAudio(toRef(props, 'screen'), gridQuestionSchema);

function playFeedbackAudio() {
  // Tier 1: explicit prop-level feedback-audio / feedback-speech
  const f = props.screen['feedback-audio'] || props.screen['feedback-speech'];
  const fs = getFeedbackString(f);

  if (fs?.text) {
    play(fs.text as string);
    return;
  }

  // Tier 2/3: schema-based — resolveSlot handles S3 (getAudioURL) or JiT
  const slotName =
    getFeedbackString(props.screen.feedback)?.audio ?? 'feedback';
  playSlot(slotName);
}

function isItemIncluded(itemIndex: number) {
  if (props.screen.survey !== 'multi') return true;

  let itemSelections = selected.value.filter(
    itemID => itemID.indexOf('_i' + itemIndex) !== -1
  );

  return itemSelections.length > 0;
}
</script>

<style scoped>
.grid-question-option__label {
  transition: 0.2s;
}

.grid-question-option__label .ripple__wave {
  @apply bg-primary-500/10;
}

.grid-question-option__checkbox {
  --auto-alpha-dur-0: 100ms;
  --auto-alpha-dur-1: 150ms;
  transition: 0.2s;
}

.grid-question-option__background.fill:not(.disable):hover {
  @apply bg-primary-500/10;
}

.grid-question-item__icon {
  @apply text-primary-500 aspect-square h-8 w-8 justify-center rounded-full p-1 text-xl;
}
</style>
